import React, { useEffect, useRef, useState } from "react";

/**
 * Stagger — toggles `.in-view` on a container the first time it enters the
 * viewport, so its direct children fade/slide in one after another
 * (see `.rstagger` rules in index.css). Robust like Reveal: shows on-mount if
 * already visible, has a fallback timer, respects reduced motion via CSS.
 */
export default function Stagger({ as: Tag = "div", className = "", children, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (el.getBoundingClientRect().top < vh * 0.95) {
      setShown(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    const fb = setTimeout(() => setShown(true), 2500);
    return () => {
      obs.disconnect();
      clearTimeout(fb);
    };
  }, [shown]);

  return (
    <Tag ref={ref} className={`rstagger ${shown ? "in-view" : ""} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}
